import { DateTime } from "luxon";
import { Fragment, useState } from "react";
import { common_utils } from "../../../../common/common_utils";
import { WithControlledTooltip } from "../../dashboard/forms/improved_components/tooltips/controlled_tooltip";
import { parseAllowLinks } from "../../dashboard/markdown_parser/markdown";

function selectText(element_id: string) {
	const doc = document;
	const text = doc.getElementById(element_id);

	//all others
	const selection = window.getSelection();
	const range = doc.createRange();
	range.selectNodeContents(text);
	selection.removeAllRanges();
	selection.addRange(range);
}

type TokenType = keyof typeof common_utils.PlaceholderTokens;

function getTokens(filter: (val: TokenType) => boolean) {
	const tokens = Object.keys(common_utils.PlaceholderTokens).filter(filter);
	return tokens as TokenType[];
}

export function TokenPage() {
	// christmas!
	const date = DateTime.fromObject(
		{ year: DateTime.now().year, month: 12, day: 31, hour: 20, minute: 0 },
		{ zone: "America/New_York" }
	);

	const context: common_utils.PlaceholderFuncContext = {
		event_name: "New Years Party",
		event_link: undefined,
		start_time: date,
		timezone: "America/New_York",
		duration_ms: date.diffNow().toMillis(),
		description: "Watch the ball drop!",
		rsvp_options: [
			{
				name: "Yes",
				emoji: "✅",
				has_reminders: false,
				user_ids: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
			},
			{
				name: "Maybe",
				emoji: "❓",
				has_reminders: false,
				user_ids: ["123", "456", "789", "4"],
			},
			{
				name: "No",
				emoji: "❌",
				has_reminders: false,
				user_ids: ["123", "456", "789"],
			},
		],
	};

	return (
		<div className="flex flex-col gap-8">
			<TokenList
				title="Basic Placeholders"
				context={context}
				tokens={[
					"$EventName",
					"$EventDescription",
					"$EventLink",
					"$EventRSVPCounts",
				]}
			/>
			<TokenList
				title="Server DateTime Placeholders"
				description={
					<>
						Server datetime placeholders are dates which are
						displayed using the server's timezone.
					</>
				}
				context={context}
				tokens={getTokens((val) => val.startsWith("$DT_"))}
			/>
			<TokenList
				title="Local DateTime Placeholders"
				description={
					<>
						Local datetime placeholders use Discord Timestamps to
						generate date text that is displayed in{" "}
						<span className="italic">
							each user's local timezone
						</span>
						. These are only supported in embed description fields.
						For more information, see{" "}
						<a
							className="manual-link"
							href="https://sesh.fyi/timestamp/"
						>
							here
						</a>
						.
						<br />
						<br />
						The following placeholders are automatically converted
						to your local timezone (
						<span className="command-example--inline">
							{Intl.DateTimeFormat().resolvedOptions().timeZone}
						</span>
						), the same as they would be in Discord.
					</>
				}
				context={context}
				tokens={getTokens((val) => val.startsWith("$TS_"))}
			/>
			<Examples
				context={context}
				examples={[
					"$EventName on $DT_DateMDY at $DT_Time $DT_TimezoneShort",
					"$EventName $TS_Relative",
					"Last day of $DT_YearNum!",
					"$EventName: $DT_Weekday night!",
				]}
			/>
		</div>
	);
}

function TokenList(props: {
	title: string | JSX.Element;
	description?: string | JSX.Element;
	tokens: TokenType[];
	context: common_utils.PlaceholderFuncContext;
}) {
	const { tokens, context } = props;

	return (
		<div className="flex flex-col gap-4">
			<div className="">
				<div className="text-xl font-semibold">{props.title}</div>
				{props.description && <div>{props.description}</div>}
			</div>
			<div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
				{tokens.map((val) => {
					let secondary_element =
						val !== "$EventLink" ? (
							parseAllowLinks(
								common_utils.PlaceholderTokens[
									val as keyof typeof common_utils.PlaceholderTokens
								].transformer(context)
							)
						) : (
							<div className="">{`{Link to Signup Message}`}</div>
						);

					return (
						<Fragment key={val}>
							<Token token={val} />
							<div>{secondary_element}</div>
						</Fragment>
					);
				})}
			</div>
		</div>
	);
}

function Token(props: { token: string }) {
	const [tooltip_enabled, setTooltipEnabled] = useState(false);

	return (
		<WithControlledTooltip
			tooltip={"copied!"}
			style={"Info"}
			disabled={!tooltip_enabled}
		>
			<div
				id={props.token}
				className="command-example--inline w-fit"
				onClick={() => {
					navigator.clipboard.writeText(props.token);
					setTooltipEnabled(true);

					setTimeout(() => {
						setTooltipEnabled(false);
					}, 1000);
					selectText(props.token);
				}}
			>
				{props.token}
			</div>
		</WithControlledTooltip>
	);
}

function Examples(props: {
	context: common_utils.PlaceholderFuncContext;
	examples: string[];
}) {
	return (
		<div className="flex flex-col gap-2">
			<div className="text-xl font-semibold">Examples</div>

			<div className="flex w-full flex-col items-center gap-1">
				{props.examples.map((val, index) => (
					<div
						key={index}
						className="flex w-full flex-col items-center  justify-center  gap-2 rounded-sm bg-command p-4"
					>
						<div className="command-example--inline w-fit">
							{parseAllowLinks(val)}
						</div>
						<i key={index} className="fas fa-arrow-down"></i>
						<div className="">
							{parseAllowLinks(
								common_utils.replacePlaceholders(
									"EmbedDescription",
									val,
									props.context
								)
							)}
						</div>
					</div>
				))}
			</div>
		</div>
	);
}
